import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import { Toaster } from "@/components/ui/sonner";
import { motion, AnimatePresence } from "framer-motion";

// App-style pages
import AppHome from "@/pages/app/AppHome";
import AppMenu from "@/pages/app/AppMenu";
import AppOrders from "@/pages/app/AppOrders";
import AppMessages from "@/pages/app/AppMessages";
import AppProfile from "@/pages/app/AppProfile";
import AppCart from "@/pages/app/AppCart";
import AppCheckout from "@/pages/app/AppCheckout";
import AppFoodSafety from "@/pages/app/AppFoodSafety";
import AppRewardsGuide from "@/pages/app/AppRewardsGuide";
import AppAuth from "@/pages/app/AppAuth";
import AuthMagic from "@/pages/app/AuthMagic";

// Admin pages
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminStock from "@/pages/admin/AdminStock";
import AdminChat from "@/pages/admin/AdminChat";
import AdminFoodSafety from "@/pages/admin/AdminFoodSafety";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminReports from "@/pages/admin/AdminReports";
import AdminLoyalty from "@/pages/admin/AdminLoyalty";
import AdminWallets from "@/pages/admin/AdminWallets";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminMenuSchedule from "@/pages/admin/AdminMenuSchedule";
import AdminKitchenPrep from "@/pages/admin/AdminKitchenPrep";
import AdminInvitations from "@/pages/admin/AdminInvitations";
import AdminPromoCodes from "@/pages/admin/AdminPromoCodes";
import AdminKDS from "@/pages/admin/AdminKDS";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminStaffScheduling from "@/pages/admin/AdminStaffScheduling";
import AdminDemandForecasting from "@/pages/admin/AdminDemandForecasting";
import AdminAcceptInvite from "@/pages/admin/AdminAcceptInvite";
import AdminDeliveryAddresses from "@/pages/admin/AdminDeliveryAddresses";
import AdminPlatformFees from "@/pages/admin/AdminPlatformFees";
import AdminPermissions from "@/pages/admin/AdminPermissions";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminAutoScheduler from "@/pages/admin/AdminAutoScheduler";
import AdminMenuAISuggestions from "@/pages/admin/AdminMenuAISuggestions";
import AdminSides from "@/pages/admin/AdminSides";
import AdminSpecials from "@/pages/admin/AdminSpecials";
import AdminDisputes from "@/pages/admin/AdminDisputes";
import DriverDashboard from "@/pages/admin/DriverDashboard";
import TwoFactorVerify from "@/pages/admin/TwoFactorVerify";

// BOH roles allowed to access the admin portal
const BOH_ROLES = ['super_admin', 'manager', 'operations_manager', 'kitchen', 'fulfilment', 'customer_support', 'finance', 'driver', 'admin', 'staff'];
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Components
import BottomNav from "@/components/app/BottomNav";
import CartDrawer from "@/components/app/CartDrawer";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import BlockedScreen from "@/components/BlockedScreen";
import { CartProvider, useCart } from "@/context/CartContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { API_URL } from "@/config/api";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (sessionId) {
      fetch(`${API_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(res => res.json())
        .then(data => {
          if (data.access_token && data.user) {
            login(data.access_token, data.user);
            if (BOH_ROLES.includes(data.user.role)) {
              navigate('/admin', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          } else {
            navigate('/auth', { replace: true });
          }
        })
        .catch(() => navigate('/auth', { replace: true }));
    } else {
      navigate('/auth', { replace: true });
    }
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-primary font-heading text-2xl">Authenticating...</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
}

function AdminProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-900">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (!BOH_ROLES.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// App Shell - Customer Portal with Bottom Nav
function AppShell() {
  const location = useLocation();
  const { isCartOpen } = useCart();
  
  // Hide bottom nav on auth and checkout pages
  const hideNav = ['/auth', '/checkout'].some(path => location.pathname.startsWith(path));

  return (
    <div className="min-h-screen bg-background pb-20">
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<AppHome />} />
          <Route path="/menu" element={<AppMenu />} />
          <Route path="/orders" element={<ProtectedRoute><AppOrders /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><AppMessages /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AppProfile /></ProtectedRoute>} />
          <Route path="/cart" element={<AppCart />} />
          <Route path="/checkout/:orderId" element={<ProtectedRoute><AppCheckout /></ProtectedRoute>} />
          <Route path="/food-safety" element={<ProtectedRoute><AppFoodSafety /></ProtectedRoute>} />
          <Route path="/rewards-guide" element={<AppRewardsGuide />} />
          <Route path="/auth" element={<AppAuth />} />
          <Route path="/auth/magic" element={<AuthMagic />} />
        </Routes>
      </AnimatePresence>
      
      {!hideNav && <BottomNav />}
      <CartDrawer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/accept-invite" element={<AdminAcceptInvite />} />
      <Route path="/admin/reset-password" element={<AdminResetPassword />} />
      <Route path="/admin/2fa-verify" element={<TwoFactorVerify />} />
      <Route path="/admin/*" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="menu" element={<AdminMenu />} />
        <Route path="menu-schedule" element={<AdminMenuSchedule />} />
        <Route path="auto-scheduler" element={<AdminAutoScheduler />} />
        <Route path="menu-ai-suggestions" element={<AdminMenuAISuggestions />} />
        <Route path="sides" element={<AdminSides />} />
        <Route path="specials" element={<AdminSpecials />} />
        <Route path="kitchen" element={<AdminKitchenPrep />} />
        <Route path="kds" element={<AdminKDS />} />
        <Route path="stock" element={<AdminStock />} />
        <Route path="chat" element={<AdminChat />} />
        <Route path="food-safety" element={<AdminFoodSafety />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="loyalty" element={<AdminLoyalty />} />
        <Route path="wallets" element={<AdminWallets />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="forecasting" element={<AdminDemandForecasting />} />
        <Route path="promo-codes" element={<AdminPromoCodes />} />
        <Route path="disputes" element={<AdminDisputes />} />
        <Route path="announcements" element={<AdminAnnouncements />} />
        <Route path="staff-scheduling" element={<AdminStaffScheduling />} />
        <Route path="delivery-addresses" element={<AdminDeliveryAddresses />} />
        <Route path="driver" element={<DriverDashboard />} />
        <Route path="platform-fees" element={<AdminPlatformFees />} />
        <Route path="permissions" element={<AdminPermissions />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="invitations" element={<AdminInvitations />} />
      </Route>
      
      {/* Password Reset Routes (Customer) */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Customer App Shell */}
      <Route path="/*" element={<AppShell />} />
    </Routes>
  );
}

function BlockedGate() {
  const { blockedInfo } = useAuth();
  if (!blockedInfo) return null;
  return <BlockedScreen message={blockedInfo} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <CartProvider>
            <div className="App min-h-screen bg-background dark:bg-slate-900 transition-colors">
              <BlockedGate />
              <AppRouter />
              <Toaster position="top-center" richColors />
            </div>
          </CartProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
